import { YieldCurveData } from './types';

// The SOFR yield-curve JSON in S3 ships with SOFR-specific tenor codes
// (SOFR1W, SOFR1M, … SOFR50Y) and a `series.*` payload indexed
// positionally against those codes. The Yield Matrix renders against a
// fixed shortlist of display labels ("3 Mo", "10 Yr", etc.), so each
// consumer needs to project the raw payload onto a display-label shape.
// This table is the SINGLE source of truth for that projection — three
// consumers (YieldMatrixContent, YieldMatrixBidDetails,
// YieldMatrixModeling) call into `normalizeYieldCurveData` rather than
// each carrying their own copy.
export const SOFR_TENOR_DISPLAY = [
  { display: '1 Mo', sofrLabel: 'SOFR1M' },
  { display: '3 Mo', sofrLabel: 'SOFR3M' },
  { display: '6 Mo', sofrLabel: 'SOFR6M' },
  { display: '1 Yr', sofrLabel: 'SOFR1Y' },
  { display: '2 Yr', sofrLabel: 'SOFR2Y' },
  { display: '3 Yr', sofrLabel: 'SOFR3Y' },
  { display: '5 Yr', sofrLabel: 'SOFR5Y' },
  { display: '10 Yr', sofrLabel: 'SOFR10Y' },
  { display: '20 Yr', sofrLabel: 'SOFR20Y' },
  { display: '30 Yr', sofrLabel: 'SOFR30Y' },
] as const;

export const SOFR_YIELD_CURVE_FILE = 'sofr_yc_chart_data.json';
export const TREASURY_YIELD_CURVE_FILE = 'yc_chart_data.json';

type RawCurveSeries = {
  Last?: (number | null)[];
  '1m Ago'?: (number | null)[];
  '6m Ago'?: (number | null)[];
};
type RawCurveData = { labels?: string[]; series?: RawCurveSeries };

// Project a raw yield-curve JSON payload onto a display-label shape that
// YieldCurveTable can consume.
//
// SOFR payloads need re-indexing because their labels are SOFR codes; a
// previous version of YieldMatrixModeling overrode `labels` with
// TIME_LABELS without re-indexing `series.*`, which surfaced SOFR3Y
// under "10 Yr" and SOFR5Y under "30 Yr" — ENG-2753.
//
// Treasury payloads already use display-shaped labels and pass through
// unchanged.
export const normalizeYieldCurveData = (
  raw: RawCurveData,
  ycChartDataFile: string,
): YieldCurveData => {
  if (ycChartDataFile !== SOFR_YIELD_CURVE_FILE) {
    return raw as YieldCurveData;
  }
  const labels = raw.labels ?? [];
  const pickSeries = (series: (number | null)[] | undefined): (number | null)[] =>
    SOFR_TENOR_DISPLAY.map((m) => {
      const idx = labels.indexOf(m.sofrLabel);
      return idx >= 0 && series ? series[idx] : null;
    });
  return {
    labels: SOFR_TENOR_DISPLAY.map((m) => m.display),
    series: {
      Last: pickSeries(raw.series?.Last),
      '1m Ago': pickSeries(raw.series?.['1m Ago']),
      '6m Ago': pickSeries(raw.series?.['6m Ago']),
    },
  };
};

// ---------------------------------------------------------------------------
// Price-yield matrix: normalization + lookup.
//
// The backend emits each matrix row as a bare positional triple
// `[rate, priceDollars, durationYears]` (GraphQL type `[[Float!]!]`, no field
// names, no units). `rate` is a decimal fraction (0.0623), `priceDollars` is
// the purchase price in WHOLE DOLLARS, `durationYears` is in years. The pool's
// unpaid balance, meanwhile, is carried in CENTS everywhere on the client.
//
// Indexing those triples by hand and reconciling the dollars/cents split at
// each call site is what produced ENG-2915 (one surface multiplied a bid
// fraction by the cents balance without the /100, landing 100x above the
// dollar-denominated matrix → every lookup fell out of bounds → blank YTM).
//
// We collapse all of that into ONE price space: price-as-percent-of-par
// (`pricePercent`, UPB = 100%). The bid input is already a percent and the
// chart x-axis already plots this exact value, so after normalization no
// surface multiplies a bid by the balance to do a lookup — the cents/dollars
// mismatch has nowhere left to live.
export type PriceYieldPoint = {
  // Purchase price as a percentage of par (UPB), e.g. 91.17. Same value the
  // chart x-axis plots, so chart, bid input, and lookup share one space.
  pricePercent: number;
  // Yield or spread as a decimal fraction (0.0623), exactly as the backend
  // emits it. Format with rateAsPercent (yield) or rateAsBps (spread).
  rate: number;
  durationYears: number;
};

export type PriceYieldLookup = { rate: number; durationYears: number };

export const rateAsPercent = (rate: number): number => rate * 100;
export const rateAsBps = (rate: number): number => rate * 10_000;

// Project the backend's raw [rate, priceDollars, durationYears][] onto
// price-percent space using the pool's unpaid balance (in CENTS). Order is
// preserved (price descending). Returns [] — handled downstream as "no data" —
// when the matrix is absent/empty (zero matching loans, ENG-2862) or the
// balance is non-positive (a zero-balance pool would divide to ±Infinity; cf.
// ENG-2477's %-divisor guard). `priceDollars * 10000 / cents` is algebraically
// `priceDollars / (cents / 100) * 100`, i.e. purchase price ÷ par × 100.
export const normalizePriceYieldMatrix = (
  rawMatrix: number[][] | null | undefined,
  unpaidBalanceCents: number,
): PriceYieldPoint[] => {
  if (rawMatrix == null || rawMatrix.length === 0 || unpaidBalanceCents <= 0) {
    return [];
  }
  return rawMatrix.map((row) => ({
    pricePercent: (row[1] * 10_000) / unpaidBalanceCents,
    rate: row[0],
    durationYears: row[2],
  }));
};

// Interpolate rate + duration at a price-percent. Points must be ordered by
// pricePercent DESCENDING (the backend emits price descending; normalization
// preserves that). Returns null when the price is outside the covered range or
// the series is empty — callers render that as "—"/blank, never a stale value.
// Folds together the old isWithinOrderedMatrix bounds check and the
// binarySearchMatrix interpolation so out-of-bounds can only ever surface as
// null at a single, shared site.
export const lookupAtPricePercent = (
  points: PriceYieldPoint[],
  pricePercent: number,
): PriceYieldLookup | null => {
  if (points.length === 0) {
    return null;
  }
  if (
    pricePercent > points[0].pricePercent ||
    pricePercent < points[points.length - 1].pricePercent
  ) {
    return null;
  }

  let left = 0;
  let right = points.length - 1;
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const midValue = points[mid].pricePercent;
    if (midValue === pricePercent) {
      return { rate: points[mid].rate, durationYears: points[mid].durationYears };
    } else if (midValue < pricePercent) {
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  // left is the closest index below the target, right the closest above.
  if (left >= points.length) {
    return { rate: points[right].rate, durationYears: points[right].durationYears };
  }
  if (right < 0) {
    return { rate: points[left].rate, durationYears: points[left].durationYears };
  }

  const lo = points[left];
  const hi = points[right];
  if (lo.pricePercent === hi.pricePercent) {
    return { rate: lo.rate, durationYears: lo.durationYears };
  }

  // Linear interpolation between the bracketing samples.
  const weightLo = (hi.pricePercent - pricePercent) / (hi.pricePercent - lo.pricePercent);
  const weightHi = (pricePercent - lo.pricePercent) / (hi.pricePercent - lo.pricePercent);
  return {
    rate: lo.rate * weightLo + hi.rate * weightHi,
    durationYears: lo.durationYears * weightLo + hi.durationYears * weightHi,
  };
};
